/**
 * TMS Job Detail Page — Full job view
 * Spec: _spec_trucking_a1_v1.md
 * Data: fetched from entity API (slug: jobs)
 */

import { useState, useEffect, useCallback } from 'react';
import { useRenderMode } from '@/hooks/useRenderMode';
import { usePageParams } from '@/hooks/usePageParams';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Container, Truck, MapPin, User, Clock, FileText,
  DollarSign, MessageSquare, Bot, CheckCircle2, AlertTriangle,
  Phone, Package, Calendar, Navigation, RefreshCcw, Edit, Loader2,
  Ship, Anchor, ArrowRightLeft, Share2, Plus, Trash2, Save, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { flattenEntityRow } from '@/lib/fms-utils';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { captureApiError } from '@/lib/api-error';
import { ComplianceField, ComplianceLegend, ComplianceScoreBadge, ComplianceChecklist, MilestoneMapPanel, useCompliance, type ComplianceData, type DomainStatus } from '@/components/compliance';
import { ShareLinkDialog } from '@/components/entity/ShareLinkDialog';
import FileSharePanel from '@/components/entity/FileSharePanel';
import { Gen5LayerStatusCard, type Gen5LayerStatusEnvelope } from '@boox/ui-foundation';

// ── Default empty job ─────────────────────────────────────────────────────────

const EMPTY_JOB = {
  id: '', record_id: '', type: '', status: 'draft', urgent: false,
  container_no: '', container_size: '', seal_no: '',
  customer: '', customer_ref: '', do_no: '',
  port: '', delivery_address: '', bl_no: '', cargo_desc: '', hs_code: '',
  lfd: '', pickup_date: '', delivery_date: '', pickup_time_window: '',
  driver_name: '', driver_phone: '', driver_plate: '', dispatcher: '',
  vessel: '', voyage: '', eta_vessel: '', discharge_date: '', available_date: '',
  lfd_container: '', terminal: '', holds: [] as string[], chassis_no: '', chassis_return_date: '',
  ar_charges: [] as { desc: string; qty: number; unit: string; rate: number; currency: string; total: number }[],
  ap_charges: [] as { desc: string; qty: number; unit: string; rate: number; currency: string; total: number }[],
  milestones: [] as { label: string; time: string | null; done: boolean; agent: string | null }[],
  documents: [] as { type: string; status: string; uploaded_at: string | null; file_name?: string; file_url?: string }[],
  agent_log: [] as { time: string; agent: string; msg: string; type: string }[],
  // v2.4 transport fields
  mode: '', direction: '', origin: '', destination: '',
  carrier: '', inco_term: '', freight_term: '', currency: '',
  etd: '', eta: '',
  commercial_status: '', payment_status: '', invoice_number: '', billing_group_status: '',
  invoice_entry_id: '',
  paid_amount: 0, outstanding_amount: 0,
};

type TmsJobDetailState = typeof EMPTY_JOB;

type JobContainer = {
  id: string;
  containerNo: string;
  sizeType: string | null;
  sealNo: string | null;
  status: string | null;
  statusAt: string | null;
  berthAt: string | null;
  dischargedAt: string | null;
  releasedAt: string | null;
  holdCode: string | null;
  lastFreeDate: string | null;
  permitStatus: string | null;
  pickupReady: boolean;
  isSoc: boolean;
  isReefer: boolean;
  isDg: boolean;
  deliveryAddress: string;
  terminal: string;
  cargoDescription: string;
  latestEvent: { statusCode: string; statusLabel: string | null; eventTime: string | null; sourceSystem: string } | null;
  createdAt: string;
};

type ContainerDraft = {
  containerNo: string;
  sizeType: string;
  sealNo: string;
  isSoc: boolean;
  isReefer: boolean;
  isDg: boolean;
  deliveryAddress: string;
  terminal: string;
  cargoDescription: string;
};

const EMPTY_DRAFT: ContainerDraft = {
  containerNo: '', sizeType: '', sealNo: '',
  isSoc: false, isReefer: false, isDg: false,
  deliveryAddress: '', terminal: '', cargoDescription: '',
};

const SIZE_TYPES = ['20GP', '40GP', '40HC', '45HC', '20RF', '40RF', '20OT', '40OT'];
type JobTripSummary = {
  id: string;
  tripType: string;
  tripRole: string;
  status: string;
  dispatchStatus: string;
  driver: string;
  vehicle: string;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
  pickupAt: string | null;
  deliverAt: string | null;
  createdAt: string | null;
};
type JobTimelineEntry = {
  id: string;
  kind: 'milestone' | 'gps' | 'event';
  tripId: string | null;
  tripType: string;
  title: string;
  description: string;
  timestamp: string | null;
  lat: number | null;
  lng: number | null;
  speedKmh: number | null;
  heading: number | null;
  eventType: string;
  source: string;
};
type JobTimelineResponse = {
  trips: JobTripSummary[];
  timeline: JobTimelineEntry[];
};

function computeLocalCompliance(job: TmsJobDetailState): ComplianceData {
  const mk = (cond: boolean): DomainStatus['status'] => cond ? 'pass' : 'warn';
  const sc = (cond: boolean) => cond ? 100 : 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uploadedDocs = job.documents.filter((d: any) => d.status === 'uploaded').length;
  const domains: DomainStatus[] = [
    { domain: 'container_id', label: 'Container ID',          score: sc(!!job.container_no),                           status: mk(!!job.container_no),                           details: job.container_no || 'Container number missing' },
    { domain: 'pod',          label: 'Proof of Delivery',     score: 0, status: 'na', details: 'Proof of delivery tracked via GPS milestones' },
    { domain: 'eir',          label: 'Equipment Interchange', score: 0, status: 'na', details: 'Equipment interchange record from terminal' },
    { domain: 'cargo_info',   label: 'Cargo Information',     score: sc(!!job.cargo_desc && !!job.hs_code),            status: mk(!!job.cargo_desc && !!job.hs_code),            details: job.cargo_desc ? `${job.cargo_desc}${job.hs_code ? ' / HS ' + job.hs_code : ''}` : 'Cargo description or HS code missing' },
    { domain: 'vgm',          label: 'Verified Gross Mass',   score: 0, status: 'na', details: 'Verified gross mass from carrier system' },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { domain: 'milestones',   label: 'Milestones & Events',   score: sc(job.milestones.length > 0),                   status: mk(job.milestones.length > 0),                   details: job.milestones.length > 0 ? `${job.milestones.filter((m: any) => m.done).length}/${job.milestones.length} milestones completed` : 'No milestones recorded' },
    { domain: 'gps',          label: 'GPS Tracking',          score: 0, status: 'na', details: 'Live GPS from driver mobile app' },
    { domain: 'time_slots',   label: 'Time Slot Booking',     score: sc(!!job.pickup_date),                            status: mk(!!job.pickup_date),                            details: job.pickup_date ? `Pickup ${job.pickup_date}` : 'No pickup date assigned' },
    { domain: 'documents',    label: 'Document & Compliance', score: sc(uploadedDocs > 0),                             status: mk(uploadedDocs > 0),                             details: `${uploadedDocs}/${job.documents.length} documents uploaded` },
    { domain: 'charges',      label: 'Charge Transparency',   score: sc(job.ar_charges.length > 0),                   status: mk(job.ar_charges.length > 0),                   details: `${job.ar_charges.length} AR, ${job.ap_charges.length} AP charge lines` },
  ];
  const active = domains.filter((d) => d.status !== 'na');
  const passed = active.filter((d) => d.status === 'pass');
  const overallScore = active.length > 0 ? Math.round(passed.length / active.length * 100) : 0;
  return { jobId: job.id, overallScore, domains, violations: [], auditLog: [] };
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-SG', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCoords(lat: number | null | undefined, lng: number | null | undefined): string {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function buildLatLngGoogleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

const COMMERCIAL_COLORS: Record<string, string> = {
  delivery_in_progress: 'bg-slate-100 text-slate-700 border-slate-200',
  proof_pending: 'bg-amber-100 text-amber-800 border-amber-200',
  ready_to_cost: 'bg-sky-100 text-sky-800 border-sky-200',
  cost_review: 'bg-violet-100 text-violet-800 border-violet-200',
  ready_to_invoice: 'bg-blue-100 text-blue-800 border-blue-200',
  invoiced: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  partial_paid: 'bg-orange-100 text-orange-800 border-orange-200',
  settled: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  disputed: 'bg-rose-100 text-rose-800 border-rose-200',
};

function labelize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function money(amount: number, currency = 'SGD') {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TmsJobDetailPage() {
  const isMobile = useRenderMode() === 'mobile';
  const { jobId } = usePageParams<{ jobId: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<TmsJobDetailState>(EMPTY_JOB);
  const [tripSummaries, setTripSummaries] = useState<JobTripSummary[]>([]);
  const [jobTimeline, setJobTimeline] = useState<JobTimelineEntry[]>([]);
  const [apiError, setApiError] = useState<DataErrorDetail | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // Containers tab state
  const [containers, setContainers] = useState<JobContainer[]>([]);
  const [containersLoading, setContainersLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ContainerDraft>(EMPTY_DRAFT);
  const [addingNew, setAddingNew] = useState(false);
  const [newDraft, setNewDraft] = useState<ContainerDraft>(EMPTY_DRAFT);
  const [containerSaving, setContainerSaving] = useState(false);
  // MilestoneMapPanel now renders inline — no toggle state needed
  const jobsListPath = location.pathname.startsWith('/hms/')
    ? '/hms/jobs'
    : location.pathname.startsWith('/tm/')
      ? '/tm/jobs'
      : '/tms/jobs';

  const loadJob = useCallback(() => {
    if (!jobId) return;
    setLoading(true);
    setApiError(null);
    Promise.all([
      api.get<Record<string, unknown>>(`/jobs/${encodeURIComponent(jobId)}`),
      api.get<JobTimelineResponse>(`/jobs/${encodeURIComponent(jobId)}/timeline`).catch(() => ({ trips: [], timeline: [] })),
    ])
      .then(([res, timelineRes]) => {
        // apiFetch() already unwraps {ok,data} envelope — res IS the data
        const d = flattenEntityRow((res as Record<string, unknown>) ?? {});
        setJob({
          id: String(d.jobNumber ?? d.job_number ?? d.jobCode ?? d.job_code ?? d.id ?? jobId),
          record_id: String(d.id ?? jobId),
          type: String(d.jobType ?? d.job_type ?? ''),
          status: String(d.status ?? 'draft'),
          urgent: Boolean(d.urgent ?? d.isUrgent ?? d.is_urgent ?? false),
          container_no: String(d.containerNo ?? d.container_no ?? ''),
          container_size: String(d.containerSize ?? d.container_size ?? ''),
          seal_no: String(d.sealNo ?? d.seal_no ?? ''),
          customer: String(d.customerName ?? d.customer_name ?? d.customer ?? ''),
          customer_ref: String(d.customerRef ?? d.customer_ref ?? ''),
          do_no: String(d.doNo ?? d.do_no ?? ''),
          port: String(d.port ?? d.portName ?? d.port_name ?? ''),
          delivery_address: String(d.deliveryAddress ?? d.delivery_address ?? ''),
          bl_no: String(d.blNo ?? d.bl_no ?? ''),
          cargo_desc: String(d.cargoDesc ?? d.cargo_desc ?? d.cargoDescription ?? ''),
          hs_code: String(d.hsCode ?? d.hs_code ?? ''),
          lfd: String(d.lfd ?? d.lastFreeDate ?? d.last_free_date ?? ''),
          pickup_date: String(d.pickupDate ?? d.pickup_date ?? ''),
          delivery_date: String(d.deliveryDate ?? d.delivery_date ?? ''),
          pickup_time_window: String(d.pickupTimeWindow ?? d.pickup_time_window ?? ''),
          driver_name: String(d.driverName ?? d.driver_name ?? ''),
          driver_phone: String(d.driverPhone ?? d.driver_phone ?? ''),
          driver_plate: String(d.driverPlate ?? d.driver_plate ?? ''),
          dispatcher: String(d.dispatcher ?? ''),
          vessel: String(d.vessel ?? d.vesselName ?? d.vessel_name ?? ''),
          voyage: String(d.voyage ?? ''),
          eta_vessel: String(d.etaVessel ?? d.eta_vessel ?? ''),
          discharge_date: String(d.dischargeDate ?? d.discharge_date ?? ''),
          available_date: String(d.availableDate ?? d.available_date ?? ''),
          lfd_container: String(d.lfdContainer ?? d.lfd_container ?? d.lfd ?? ''),
          terminal: String(d.terminal ?? ''),
          holds: Array.isArray(d.holds) ? d.holds as string[] : [],
          chassis_no: String(d.chassisNo ?? d.chassis_no ?? ''),
          chassis_return_date: String(d.chassisReturnDate ?? d.chassis_return_date ?? ''),
          ar_charges: Array.isArray(d.arCharges ?? d.ar_charges) ? (d.arCharges ?? d.ar_charges) as typeof EMPTY_JOB.ar_charges : [],
          ap_charges: Array.isArray(d.apCharges ?? d.ap_charges) ? (d.apCharges ?? d.ap_charges) as typeof EMPTY_JOB.ap_charges : [],
          milestones: Array.isArray(d.milestones) ? d.milestones as typeof EMPTY_JOB.milestones : [],
          documents: Array.isArray(d.documents) ? d.documents as typeof EMPTY_JOB.documents : [],
          agent_log: Array.isArray(d.agentLog ?? d.agent_log) ? (d.agentLog ?? d.agent_log) as typeof EMPTY_JOB.agent_log : [],
          mode: String(d.mode ?? ''),
          direction: String(d.direction ?? ''),
          origin: String(d.origin ?? d.pol ?? ''),
          destination: String(d.destination ?? d.pod ?? ''),
          carrier: String(d.carrierName ?? d.carrier_name ?? d.carrier ?? ''),
          inco_term: String(d.incoTerm ?? d.inco_term ?? ''),
          freight_term: String(d.freightTerm ?? d.freight_term ?? ''),
          currency: String(d.currency ?? ''),
          etd: String(d.etd ?? ''),
          eta: String(d.eta ?? d.etaVessel ?? d.eta_vessel ?? ''),
          commercial_status: String(d.commercial_status ?? d.commercialStatus ?? ''),
          payment_status: String(d.payment_status ?? d.paymentStatus ?? ''),
          invoice_number: String(d.invoice_number ?? d.invoiceNumber ?? ''),
          invoice_entry_id: String(d.invoice_entry_id ?? d.invoiceEntryId ?? ''),
          billing_group_status: String(d.billing_group_status ?? d.billingGroupStatus ?? ''),
          paid_amount: Number(d.paid_amount ?? d.paidAmount ?? 0),
          outstanding_amount: Number(d.outstanding_amount ?? d.outstandingAmount ?? 0),
        });
        setTripSummaries(Array.isArray(timelineRes.trips) ? timelineRes.trips : []);
        setJobTimeline(Array.isArray(timelineRes.timeline) ? timelineRes.timeline : []);
      })
      .catch((err) => {
        setApiError(captureApiError(err, `/jobs/${jobId}`));
        setJob(EMPTY_JOB);
        setTripSummaries([]);
        setJobTimeline([]);
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  const loadContainers = useCallback(() => {
    if (!jobId) return;
    setContainersLoading(true);
    api.get<{ containers: JobContainer[] }>(`/jobs/${encodeURIComponent(jobId)}/containers`)
      .then((res) => {
        const raw = res as unknown as { containers: JobContainer[] };
        const list = Array.isArray(raw?.containers) ? raw.containers : [];
        const getField = (c: unknown, key: string): string => {
          const r = c as Record<string, unknown>;
          const d = (r.data ?? {}) as Record<string, unknown>;
          return String(r[key] ?? d[key] ?? '');
        };
        setContainers(list.map((c) => ({
          ...c,
          deliveryAddress: getField(c, 'deliveryAddress'),
          terminal: getField(c, 'terminal'),
          cargoDescription: getField(c, 'cargoDescription'),
        })));
      })
      .catch(() => setContainers([]))
      .finally(() => setContainersLoading(false));
  }, [jobId]);

  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => { loadContainers(); }, [loadContainers]);

  const arTotal = job.ar_charges.reduce((s, c) => s + c.total, 0);
  const apTotal = job.ap_charges.reduce((s, c) => s + c.total, 0);
  const compliance = useCompliance(jobId);
  const displayCompliance = (!compliance.loading && compliance.data.domains.every((d) => d.status === 'na'))
    ? computeLocalCompliance(job)
    : compliance.data;
  const timelineMilestones = jobTimeline.filter((entry) => entry.kind === 'milestone');
  const timelineEvents = jobTimeline.filter((entry) => entry.kind === 'event');
  const settlementTab = ['invoiced', 'partial_paid', 'settled', 'disputed'].includes(job.commercial_status)
    ? 'ar'
    : 'closure';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-3">
        <ShareLinkDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          entitySlug="jobs"
          entityId={jobId}
          recordTitle={job.id || 'Job'}
          record={job as unknown as Record<string, unknown>}
        />
        {apiError && <DataErrorBanner error={apiError} entityLabel="作业详情 (Job Detail)" onRetry={loadJob} compact />}
        {/* Back + header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-8 px-2 shrink-0" asChild>
            <Link to={jobsListPath}><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono font-bold text-sm">{job.id}</span>
              <Badge className="text-[9px] bg-blue-100 text-blue-700 border-blue-200">{job.type}</Badge>
              <Badge className="text-[9px] bg-yellow-100 text-yellow-700">{job.status.replace(/_/g, ' ')}</Badge>
              {job.urgent && <Badge variant="destructive" className="text-[9px]">URGENT</Badge>}
              <ComplianceScoreBadge score={displayCompliance.overallScore} />
            </div>
            <div className="font-mono text-xs text-muted-foreground mt-0.5 truncate">{job.container_no} · {job.customer}</div>
          </div>
        </div>
        {/* Key info cards */}
        <div className="grid grid-cols-2 gap-2">
          <Card className="overflow-hidden"><CardContent className="p-3">
            <div className="text-[10px] text-muted-foreground">Container</div>
            <div className="font-mono text-sm font-bold">{job.container_no}</div>
            <div className="text-xs text-muted-foreground">{job.container_size} · Seal {job.seal_no}</div>
          </CardContent></Card>
          <Card className="overflow-hidden"><CardContent className="p-3">
            <div className="text-[10px] text-muted-foreground">LFD</div>
            <div className="text-sm font-bold text-orange-600">{job.lfd}</div>
            <div className="text-xs text-muted-foreground">Delivery {job.delivery_date}</div>
          </CardContent></Card>
          <Card className="overflow-hidden"><CardContent className="p-3">
            <div className="text-[10px] text-muted-foreground">Port</div>
            <div className="text-xs font-semibold leading-tight">{job.port}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">BL: {job.bl_no}</div>
          </CardContent></Card>
          <Card className="overflow-hidden"><CardContent className="p-3">
            <div className="text-[10px] text-muted-foreground">Driver</div>
            <div className="text-xs font-semibold">{job.driver_name}</div>
            <div className="text-[10px] text-muted-foreground">{job.driver_phone}</div>
          </CardContent></Card>
        </div>
        {/* Route */}
        <Card><CardContent className="p-3 space-y-2">
          <div className="text-xs font-semibold">Route</div>
          <div className="flex items-start gap-2 text-xs">
            <MapPin className="h-3.5 w-3.5 text-blue-600 shrink-0 mt-0.5" />
            <div><div className="text-muted-foreground text-[10px]">Pick up</div>{job.port}</div>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <Navigation className="h-3.5 w-3.5 text-green-600 shrink-0 mt-0.5" />
            <div><div className="text-muted-foreground text-[10px]">Deliver to</div>{job.delivery_address}</div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Package className="h-3.5 w-3.5" /> {job.cargo_desc}<span className="ml-1 font-mono">HS {job.hs_code}</span>
          </div>
        </CardContent></Card>
        {/* Charges summary */}
        <Card><CardContent className="p-3">
          <div className="text-xs font-semibold mb-2">Charges</div>
          <div className="flex justify-between text-sm">
            <span className="text-green-700">AR Total</span>
            <span className="font-bold">S${arTotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-red-600">AP Total</span>
            <span className="font-bold">S${apTotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm mt-1 pt-1 border-t">
            <span>Margin</span>
            <span className={`font-bold ${arTotal - apTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>S${(arTotal - apTotal).toLocaleString()}</span>
          </div>
        </CardContent></Card>
        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" className="h-10"><FileText className="mr-1.5 h-4 w-4" /> Invoice</Button>
          <Button size="sm" variant="outline" className="h-10"><RefreshCcw className="mr-1.5 h-4 w-4" /> Sync Port</Button>
          <Button size="sm" variant="outline" className="h-10"><Phone className="mr-1.5 h-4 w-4" /> Call Driver</Button>
          <Button size="sm" variant="outline" className="h-10"><MessageSquare className="mr-1.5 h-4 w-4" /> Notify</Button>
          <Button size="sm" variant="outline" className="h-10 col-span-2" onClick={() => setShareDialogOpen(true)}><Share2 className="mr-1.5 h-4 w-4" /> Share</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-6xl">
      <ShareLinkDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        entitySlug="jobs"
        entityId={jobId}
        recordTitle={job.id || 'Job'}
        record={job as unknown as Record<string, unknown>}
      />
      {apiError && <DataErrorBanner error={apiError} entityLabel="作业详情 (Job Detail)" onRetry={loadJob} />}
      {/* ── Breadcrumb Header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
            <Link to={jobsListPath}><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold">{job.id}</h2>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">{job.type}</Badge>
              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 text-[10px]">
                {job.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </Badge>
              {job.commercial_status ? (
                <Badge className={`text-[10px] ${COMMERCIAL_COLORS[job.commercial_status] ?? 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                  {labelize(job.commercial_status)}
                </Badge>
              ) : null}
              {job.urgent && <Badge variant="destructive" className="text-[10px]">URGENT</Badge>}
              <ComplianceScoreBadge score={displayCompliance.overallScore} size="md" />
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Container <span className="font-mono font-medium">{job.container_no}</span> · {job.customer}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Ref {job.customer_ref || '—'} · Vessel {job.vessel || '—'} / {job.voyage || '—'} · Terminal {job.terminal || job.port || '—'} · ETA {String(job.eta || job.eta_vessel || '').slice(0, 10) || '—'}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8">
            <RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Sync Port
          </Button>
          <Button variant="outline" size="sm" className="h-8" asChild>
            <Link to={`/tm/jobs/${jobId}/edit`}>
              <Edit className="mr-1.5 h-3.5 w-3.5" /> Edit Job
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setShareDialogOpen(true)}>
            <Share2 className="mr-1.5 h-3.5 w-3.5" /> Share
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold text-foreground">TM Job Context</span>
            <Badge variant="outline" className="capitalize">
              {job.status.replace(/_/g, ' ')}
            </Badge>
            {job.commercial_status ? (
              <Badge className={COMMERCIAL_COLORS[job.commercial_status] ?? 'bg-slate-100 text-slate-700 border-slate-200'}>
                {labelize(job.commercial_status)}
              </Badge>
            ) : null}
            {job.payment_status ? <Badge variant="outline">{labelize(job.payment_status)}</Badge> : null}
            {job.billing_group_status ? <Badge variant="outline">{labelize(job.billing_group_status)}</Badge> : null}
            <span className="text-muted-foreground">Invoice {job.invoice_number || '—'}</span>
            <span className="text-muted-foreground">Paid {money(job.paid_amount)}</span>
            <span className="text-muted-foreground">Outstanding {money(job.outstanding_amount)}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/tm/commercial-queue?jobNumber=${encodeURIComponent(job.id)}`}>Open Queue</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/tm/billing?jobNumber=${encodeURIComponent(job.id)}&jobId=${encodeURIComponent(job.record_id || jobId)}`}>Open Billing</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/tm/settlement?tab=${settlementTab}&jobNumber=${encodeURIComponent(job.id)}&jobId=${encodeURIComponent(job.record_id || jobId)}`}>Open Settlement</Link>
            </Button>
            {job.invoice_entry_id ? (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/e/ledger-entries/${encodeURIComponent(job.invoice_entry_id)}`}>Open Ledger</Link>
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── Main Detail Grid ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Left: Info cards */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs defaultValue="details">
            <TabsList className="h-8 text-xs">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="containers" className="gap-1">
                <Container className="h-3 w-3" />
                Containers
                {containers.length > 0 && (
                  <span className="ml-0.5 rounded-full bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0">{containers.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="trips">Trips / GPS</TabsTrigger>
              <TabsTrigger value="terminal">Terminal Data</TabsTrigger>
              <TabsTrigger value="charges">Charges</TabsTrigger>
              <TabsTrigger value="docs">Documents</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
              <TabsTrigger value="ai">AI Activity</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
            </TabsList>

            {/* ── Details Tab ─────────────────────────────────────────── */}
            <TabsContent value="details" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Job Reference */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-primary" /> Job Reference
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <ComplianceField>
                      {[['BL No', <span className="font-mono">{job.bl_no}</span>],
                        ['DO No', job.do_no],
                        ['Cargo', job.cargo_desc],
                        ['HS Code', job.hs_code],
                      ].map(([k, v]) => (
                        <div key={k as string} className="flex justify-between text-xs py-0.5">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-medium text-right max-w-[60%]">{v}</span>
                        </div>
                      ))}
                    </ComplianceField>
                    <ComplianceLegend compact />
                  </CardContent>
                </Card>

                {/* Route */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <Navigation className="h-3.5 w-3.5 text-primary" /> Route
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    <ComplianceField>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-3.5 w-3.5 text-orange-500 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-medium">Pick Up</div>
                          <div className="text-muted-foreground">{job.port}</div>
                          <div className="text-muted-foreground">{job.pickup_date} {job.pickup_time_window}</div>
                        </div>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex items-start gap-2">
                        <MapPin className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-medium">Delivery</div>
                          <div className="text-muted-foreground">{job.delivery_address}</div>
                          <div className="text-muted-foreground">{job.delivery_date}</div>
                        </div>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">LFD</span>
                        <span className="font-semibold text-orange-600">{job.lfd_container}</span>
                      </div>
                    </ComplianceField>
                  </CardContent>
                </Card>

                {/* Driver */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-primary" /> Driver / Vehicle
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    <ComplianceField>
                      {[
                        ['Driver', job.driver_name],
                        ['Phone', job.driver_phone],
                        ['Vehicle', job.driver_plate],
                        ['Chassis', job.chassis_no],
                        ['Chassis Return', job.chassis_return_date],
                        ['Assigned By', job.dispatcher],
                      ].map(([k, v]) => (
                        <div key={k as string} className="flex justify-between py-0.5">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-medium">{v}</span>
                        </div>
                      ))}
                    </ComplianceField>
                  </CardContent>
                </Card>

                {/* Customer */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5 text-primary" /> Customer
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    {[
                      ['Customer', job.customer],
                      ['Reference', job.customer_ref],
                      ['DO No', job.do_no],
                      ['Terminal', job.terminal || job.port || '—'],
                      ['ETA', job.eta ? job.eta.toString().slice(0, 10) : (job.eta_vessel ? job.eta_vessel.toString().slice(0, 10) : '—')],
                    ].map(([k, v]) => (
                      <div key={k as string} className="flex justify-between">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Commercial */}
                {job.commercial_status ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <DollarSign className="h-3.5 w-3.5 text-primary" /> Commercial
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    {[
                      ['Commercial', labelize(job.commercial_status)],
                      ['Payment', job.payment_status ? labelize(job.payment_status) : '—'],
                      ['Invoice', job.invoice_number || '—'],
                      ['Billing Group', job.billing_group_status ? labelize(job.billing_group_status) : '—'],
                      ['Paid', money(job.paid_amount)],
                      ['Outstanding', money(job.outstanding_amount)],
                    ].map(([k, v]) => (
                      <div key={k as string} className="flex justify-between">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/tm/commercial-queue?jobNumber=${encodeURIComponent(job.id)}`}>Open Queue</Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/tm/billing?jobNumber=${encodeURIComponent(job.id)}&jobId=${encodeURIComponent(job.record_id || jobId)}`}>Open Billing</Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/tm/settlement?tab=${settlementTab}&jobNumber=${encodeURIComponent(job.id)}&jobId=${encodeURIComponent(job.record_id || jobId)}`}>Open Settlement</Link>
                      </Button>
                      {job.invoice_entry_id ? (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/e/ledger-entries/${encodeURIComponent(job.invoice_entry_id)}`}>Open Ledger</Link>
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
                ) : null}

                {/* Transport Info (v2.4) */}
                {(job.mode || job.origin || job.carrier) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <Ship className="h-3.5 w-3.5 text-primary" /> Transport
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    {[
                      ['Mode', job.mode ? job.mode.toUpperCase() : '—'],
                      ['Direction', job.direction ? job.direction.replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '—'],
                      ['Route', (job.origin || job.destination) ? `${job.origin} → ${job.destination}` : '—'],
                      ['Carrier', job.carrier || '—'],
                      ['Incoterm', job.inco_term || '—'],
                      ['Freight Term', job.freight_term ? job.freight_term.replace(/\b\w/g, (c: string) => c.toUpperCase()) : '—'],
                      ['Currency', job.currency || '—'],
                      ['ETD', job.etd ? job.etd.toString().slice(0, 10) : '—'],
                      ['ETA', job.eta ? job.eta.toString().slice(0, 10) : '—'],
                    ].map(([k, v]) => (
                      <div key={k as string} className="flex justify-between">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                )}
              </div>
            </TabsContent>

            {/* ── Containers Tab ──────────────────────────────────── */}
            <TabsContent value="containers" className="mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <Container className="h-3.5 w-3.5 text-primary" /> FCL Containers
                      {containersLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                    </CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      disabled={addingNew}
                      onClick={() => { setAddingNew(true); setNewDraft(EMPTY_DRAFT); }}
                    >
                      <Plus className="h-3.5 w-3.5" /> Add Container
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {/* Empty state */}
                  {containers.length === 0 && !addingNew && !containersLoading && (
                    <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                      <Container className="h-8 w-8 mb-2 opacity-20" />
                      <p className="text-sm font-medium">No containers yet</p>
                      <p className="text-xs mt-0.5">Click &ldquo;Add Container&rdquo; above to attach containers to this job</p>
                    </div>
                  )}
                  {/* Existing container rows */}
                  {containers.map((c) => {
                    const isEditing = editingId === c.id;
                    return (
                      <div key={c.id} className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        {!isEditing ? (
                          /* --- VIEW MODE --- */
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono font-bold text-sm">{c.containerNo}</span>
                                {c.sizeType && <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px]">{c.sizeType}</Badge>}
                                {c.status && c.status !== 'UNKNOWN' && (
                                  <Badge className={`text-[10px] ${
                                    c.status === 'AVAILABLE' || c.status === 'DISCHARGED' ? 'bg-green-100 text-green-800 border-green-300' :
                                    c.status === 'COLLECTED' || c.status === 'GATE_OUT' ? 'bg-gray-100 text-gray-700 border-gray-300' :
                                    c.status === 'HOLD' ? 'bg-red-100 text-red-700 border-red-300' :
                                    'bg-yellow-100 text-yellow-800 border-yellow-300'
                                  }`}>{c.status}</Badge>
                                )}
                                {c.isDg && <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px]">DG</Badge>}
                                {c.isReefer && <Badge className="bg-cyan-100 text-cyan-700 border-cyan-300 text-[10px]">REEFER</Badge>}
                                {c.isSoc && <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-[10px]">SOC</Badge>}
                                {c.pickupReady && <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px]">Pickup Ready</Badge>}
                              </div>
                              <div className="flex gap-1.5">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => {
                                    setEditingId(c.id);
                                    setEditDraft({
                                      containerNo: c.containerNo,
                                      sizeType: c.sizeType ?? '',
                                      sealNo: c.sealNo ?? '',
                                      isSoc: c.isSoc,
                                      isReefer: c.isReefer,
                                      isDg: c.isDg,
                                      deliveryAddress: c.deliveryAddress,
                                      terminal: c.terminal,
                                      cargoDescription: c.cargoDescription,
                                    });
                                  }}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                  onClick={async () => {
                                    if (!confirm(`Delete container ${c.containerNo}?`)) return;
                                    await api.delete(`/jobs/${jobId}/containers/${c.id}`);
                                    loadContainers();
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                              {c.sealNo && <div><span className="text-muted-foreground">Seal </span><span className="font-mono">{c.sealNo}</span></div>}
                              {c.lastFreeDate && <div><span className="text-muted-foreground">LFD </span><span className="font-semibold text-orange-600">{String(c.lastFreeDate).slice(0, 10)}</span></div>}
                              {c.dischargedAt && <div><span className="text-muted-foreground">Discharged </span><span>{String(c.dischargedAt).slice(0, 10)}</span></div>}
                              {c.releasedAt && <div><span className="text-muted-foreground">Released </span><span>{String(c.releasedAt).slice(0, 10)}</span></div>}
                              {c.holdCode && <div><span className="text-red-600 font-semibold">Hold: {c.holdCode}</span></div>}
                              {c.deliveryAddress && <div className="col-span-2"><span className="text-muted-foreground">Delivery </span><span>{c.deliveryAddress}</span></div>}
                              {c.terminal && <div><span className="text-muted-foreground">Terminal </span><span>{c.terminal}</span></div>}
                              {c.cargoDescription && <div className="col-span-2"><span className="text-muted-foreground">Cargo </span><span>{c.cargoDescription}</span></div>}
                            </div>
                            {c.latestEvent && (
                              <div className="text-[10px] text-muted-foreground border-t border-blue-200 pt-1.5 mt-1">
                                Last event: <span className="font-medium">{c.latestEvent.statusLabel ?? c.latestEvent.statusCode}</span>
                                {c.latestEvent.eventTime && ` · ${String(c.latestEvent.eventTime).slice(0, 16).replace('T', ' ')}`}
                                <span className="ml-1 opacity-60">[{c.latestEvent.sourceSystem}]</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* --- EDIT MODE --- */
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-0.5">Container No *</div>
                                <Input
                                  className="h-7 text-xs font-mono"
                                  value={editDraft.containerNo}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, containerNo: e.target.value.toUpperCase() }))}
                                />
                              </div>
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-0.5">Size / Type</div>
                                <select
                                  className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                                  value={editDraft.sizeType}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, sizeType: e.target.value }))}
                                >
                                  <option value="">— Select —</option>
                                  {SIZE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-0.5">Seal No</div>
                                <Input
                                  className="h-7 text-xs"
                                  value={editDraft.sealNo}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, sealNo: e.target.value }))}
                                />
                              </div>
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-0.5">Delivery Address</div>
                                <Input
                                  className="h-7 text-xs"
                                  value={editDraft.deliveryAddress}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, deliveryAddress: e.target.value }))}
                                />
                              </div>
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-0.5">Terminal</div>
                                <Input
                                  className="h-7 text-xs"
                                  value={editDraft.terminal}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, terminal: e.target.value }))}
                                />
                              </div>
                              <div>
                                <div className="text-[10px] text-muted-foreground mb-0.5">Cargo Description</div>
                                <Input
                                  className="h-7 text-xs"
                                  value={editDraft.cargoDescription}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, cargoDescription: e.target.value }))}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={editDraft.isDg} onChange={(e) => setEditDraft((d) => ({ ...d, isDg: e.target.checked }))} className="h-3.5 w-3.5" />
                                DG
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={editDraft.isReefer} onChange={(e) => setEditDraft((d) => ({ ...d, isReefer: e.target.checked }))} className="h-3.5 w-3.5" />
                                Reefer
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={editDraft.isSoc} onChange={(e) => setEditDraft((d) => ({ ...d, isSoc: e.target.checked }))} className="h-3.5 w-3.5" />
                                SOC
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="h-7 text-xs gap-1"
                                disabled={containerSaving}
                                onClick={async () => {
                                  if (!editDraft.containerNo.trim()) return;
                                  setContainerSaving(true);
                                  try {
                                    await api.patch(`/jobs/${jobId}/containers/${c.id}`, {
                                      containerNo: editDraft.containerNo,
                                      sizeType: editDraft.sizeType || null,
                                      sealNo: editDraft.sealNo || null,
                                      isDg: editDraft.isDg,
                                      isReefer: editDraft.isReefer,
                                      isSoc: editDraft.isSoc,
                                      deliveryAddress: editDraft.deliveryAddress,
                                      terminal: editDraft.terminal,
                                      cargoDescription: editDraft.cargoDescription,
                                    });
                                    setEditingId(null);
                                    loadContainers();
                                  } finally {
                                    setContainerSaving(false);
                                  }
                                }}
                              >
                                {containerSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setEditingId(null)}>
                                <X className="h-3 w-3" /> Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add new container row */}
                  {addingNew && (
                    <div className="rounded-lg border border-blue-400 bg-blue-100 p-3 space-y-3">
                      <div className="text-xs font-semibold text-blue-700">New Container</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Container No *</div>
                          <Input
                            className="h-7 text-xs font-mono"
                            placeholder="e.g. TCKU1234567"
                            value={newDraft.containerNo}
                            onChange={(e) => setNewDraft((d) => ({ ...d, containerNo: e.target.value.toUpperCase() }))}
                          />
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Size / Type</div>
                          <select
                            className="h-7 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                            value={newDraft.sizeType}
                            onChange={(e) => setNewDraft((d) => ({ ...d, sizeType: e.target.value }))}
                          >
                            <option value="">— Select —</option>
                            {SIZE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Seal No</div>
                          <Input
                            className="h-7 text-xs"
                            value={newDraft.sealNo}
                            onChange={(e) => setNewDraft((d) => ({ ...d, sealNo: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Delivery Address</div>
                          <Input
                            className="h-7 text-xs"
                            value={newDraft.deliveryAddress}
                            onChange={(e) => setNewDraft((d) => ({ ...d, deliveryAddress: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Terminal</div>
                          <Input
                            className="h-7 text-xs"
                            value={newDraft.terminal}
                            onChange={(e) => setNewDraft((d) => ({ ...d, terminal: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Cargo Description</div>
                          <Input
                            className="h-7 text-xs"
                            value={newDraft.cargoDescription}
                            onChange={(e) => setNewDraft((d) => ({ ...d, cargoDescription: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={newDraft.isDg} onChange={(e) => setNewDraft((d) => ({ ...d, isDg: e.target.checked }))} className="h-3.5 w-3.5" />
                          DG
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={newDraft.isReefer} onChange={(e) => setNewDraft((d) => ({ ...d, isReefer: e.target.checked }))} className="h-3.5 w-3.5" />
                          Reefer
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={newDraft.isSoc} onChange={(e) => setNewDraft((d) => ({ ...d, isSoc: e.target.checked }))} className="h-3.5 w-3.5" />
                          SOC
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="h-7 text-xs gap-1"
                          disabled={containerSaving || !newDraft.containerNo.trim()}
                          onClick={async () => {
                            setContainerSaving(true);
                            try {
                              await api.post(`/jobs/${jobId}/containers`, {
                                containerNo: newDraft.containerNo,
                                sizeType: newDraft.sizeType || null,
                                sealNo: newDraft.sealNo || null,
                                isDg: newDraft.isDg,
                                isReefer: newDraft.isReefer,
                                isSoc: newDraft.isSoc,
                                deliveryAddress: newDraft.deliveryAddress,
                                terminal: newDraft.terminal,
                                cargoDescription: newDraft.cargoDescription,
                              });
                              setAddingNew(false);
                              setNewDraft(EMPTY_DRAFT);
                              loadContainers();
                            } finally {
                              setContainerSaving(false);
                            }
                          }}
                        >
                          {containerSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs gap-1"
                          onClick={() => { setAddingNew(false); setNewDraft(EMPTY_DRAFT); }}
                        >
                          <X className="h-3 w-3" /> Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {!containersLoading && containers.length === 0 && !addingNew && (
                    <div className="py-6 text-center text-xs text-muted-foreground">
                      No containers attached to this job yet. Click "Add Container" to add one.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Trips / GPS Tab ───────────────────────────────────── */}
            <TabsContent value="trips" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5 text-primary" /> Per Trip Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tripSummaries.length === 0 ? (
                    <div className="text-xs text-muted-foreground">No trip records for this job.</div>
                  ) : tripSummaries.map((trip) => {
                    const tripEntries = jobTimeline.filter((entry) => entry.tripId === trip.id);
                    return (
                      <div key={trip.id} className="rounded-lg border p-3">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">{trip.tripType || 'TRIP'}</Badge>
                              {trip.tripRole ? <Badge variant="outline" className="text-[10px]">{trip.tripRole}</Badge> : null}
                              <Badge variant="secondary" className="text-[10px]">{trip.status || '—'}</Badge>
                              {trip.dispatchStatus ? <Badge variant="outline" className="text-[10px]">{trip.dispatchStatus}</Badge> : null}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Driver {trip.driver || '—'} · Vehicle {trip.vehicle || '—'}
                            </div>
                          </div>
                          <div className="text-[11px] text-muted-foreground text-right">
                            <div>Planned {formatDateTime(trip.plannedStartAt)} → {formatDateTime(trip.plannedEndAt)}</div>
                            <div>Pickup {formatDateTime(trip.pickupAt)} · Delivery {formatDateTime(trip.deliverAt)}</div>
                          </div>
                        </div>
                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b text-muted-foreground">
                                <th className="text-left py-1.5">Time</th>
                                <th className="text-left py-1.5">Type</th>
                                <th className="text-left py-1.5">Title</th>
                                <th className="text-left py-1.5">GPS</th>
                                <th className="text-left py-1.5">Map</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tripEntries.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="py-3 text-muted-foreground">No timeline rows for this trip.</td>
                                </tr>
                              ) : tripEntries.map((entry) => (
                                <tr key={entry.id} className="border-b last:border-0 align-top">
                                  <td className="py-2 whitespace-nowrap">{formatDateTime(entry.timestamp)}</td>
                                  <td className="py-2 whitespace-nowrap">
                                    <Badge variant="outline" className="text-[10px]">{entry.kind.toUpperCase()}</Badge>
                                  </td>
                                  <td className="py-2">
                                    <div className="font-medium">{entry.title}</div>
                                    <div className="text-muted-foreground">{entry.description || entry.eventType || '—'}</div>
                                    {entry.speedKmh != null || entry.heading != null ? (
                                      <div className="text-[10px] text-muted-foreground mt-0.5">
                                        Speed {entry.speedKmh ?? '—'} km/h · Heading {entry.heading ?? '—'}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="py-2 font-mono">{formatCoords(entry.lat, entry.lng)}</td>
                                  <td className="py-2">
                                    {entry.lat != null && entry.lng != null ? (
                                      <a
                                        href={buildLatLngGoogleMapsUrl(entry.lat, entry.lng)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary underline underline-offset-2"
                                      >
                                        Open Map
                                      </a>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Terminal Data Tab ────────────────────────────────────── */}
            <TabsContent value="terminal" className="mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-semibold flex items-center gap-2">
                    <Bot className="h-3.5 w-3.5 text-blue-500" /> PSA PortNet Data — Auto-tracked by Tracker Agent
                    <Badge className="ml-auto text-[10px] bg-green-100 text-green-700">Live</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ComplianceField>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { label: 'Vessel', value: job.vessel, ok: true },
                        { label: 'Voyage', value: job.voyage, ok: true },
                        { label: 'Terminal', value: job.terminal, ok: true },
                        { label: 'ETA Vessel', value: job.eta_vessel, ok: true },
                        { label: 'Discharge Date', value: job.discharge_date, ok: true },
                        { label: 'Available Date', value: job.available_date, ok: true },
                        { label: 'LFD (Container)', value: job.lfd_container, ok: true },
                        { label: 'Holds', value: job.holds.length === 0 ? 'None ✅' : job.holds.join(', '), ok: job.holds.length === 0 },
                        { label: 'Chassis No', value: job.chassis_no, ok: true },
                      ].map((item) => (
                        <div key={item.label} className="rounded-lg border bg-muted/20 p-3">
                          <div className="text-[10px] text-muted-foreground">{item.label}</div>
                          <div className={`font-mono text-xs font-semibold mt-0.5 ${!item.ok ? 'text-red-600' : ''}`}>
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ComplianceField>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Charges Tab ─────────────────────────────────────────── */}
            <TabsContent value="charges" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold">AR (Invoice to Customer)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-left pb-1">Description</th>
                          <th className="text-right pb-1">Rate</th>
                          <th className="text-right pb-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {job.ar_charges.map((c, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5">{c.desc}</td>
                            <td className="text-right text-muted-foreground">{c.currency} {c.rate.toFixed(2)}</td>
                            <td className="text-right font-medium">{c.total > 0 ? `SGD ${c.total.toFixed(2)}` : '—'}</td>
                          </tr>
                        ))}
                        <tr className="font-bold">
                          <td colSpan={2} className="pt-2 text-right">Total AR</td>
                          <td className="pt-2 text-right text-green-700">SGD {arTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-semibold">AP (Cost from Driver/Carrier)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-left pb-1">Description</th>
                          <th className="text-right pb-1">Rate</th>
                          <th className="text-right pb-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {job.ap_charges.map((c, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5">{c.desc}</td>
                            <td className="text-right text-muted-foreground">SGD {c.rate.toFixed(2)}</td>
                            <td className="text-right font-medium">SGD {c.total.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="font-bold">
                          <td colSpan={2} className="pt-2 text-right">Total AP</td>
                          <td className="pt-2 text-right text-red-700">SGD {apTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-muted/30">
                <CardContent className="pt-4 pb-3">
                  <div className="flex justify-around text-center">
                    <div>
                      <div className="text-xs text-muted-foreground">Revenue</div>
                      <div className="text-lg font-bold text-green-700">SGD {arTotal.toFixed(2)}</div>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div>
                      <div className="text-xs text-muted-foreground">Cost</div>
                      <div className="text-lg font-bold text-red-700">SGD {apTotal.toFixed(2)}</div>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div>
                      <div className="text-xs text-muted-foreground">GP</div>
                      <div className="text-lg font-bold text-primary">SGD {(arTotal - apTotal).toFixed(2)}</div>
                    </div>
                    <Separator orientation="vertical" className="h-10" />
                    <div>
                      <div className="text-xs text-muted-foreground">Margin %</div>
                      <div className="text-lg font-bold">{((arTotal - apTotal) / arTotal * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Documents Tab ───────────────────────────────────────── */}
            <TabsContent value="docs" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {job.documents.map((doc) => (
                      <div key={doc.type} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{doc.type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.status === 'uploaded' ? (
                            <>
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                              <span className="text-xs text-muted-foreground">{doc.uploaded_at}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              disabled={!doc.file_url}
                              onClick={() => doc.file_url && window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
                            >View</Button>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                              <span className="text-xs text-muted-foreground">Pending</span>
                              <Button variant="outline" size="sm" className="h-7 text-xs">Upload</Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── Compliance Tab ────────────────────────────────────── */}
            <TabsContent value="compliance" className="mt-4">
              <ComplianceChecklist data={displayCompliance} loading={compliance.loading} />
            </TabsContent>

            {/* ── AI Activity Tab ────────────────────────────────────── */}
            <TabsContent value="ai" className="mt-4">
              {/* P1-4: Gen5 Layer Status Card — shows latest brain run context for this job */}
              {job.agent_log.length > 0 && (() => {
                const latestRun = job.agent_log[job.agent_log.length - 1];
                const jobRunId = `job:tms:${jobId}:${latestRun?.time?.replace(/[^0-9A-Za-z]/g, '') ?? 'latest'}`;
                const hasError = job.agent_log.some((e) => e.type === 'error');
                const envelope: Gen5LayerStatusEnvelope = {
                  runId: jobRunId,
                  taskCategory: 'tms_job_agent',
                  riskLevel: hasError ? 'medium' : 'low',
                  executionMode: 'autonomous',
                  layers: {
                    context: {
                      label: 'Job Context',
                      status: job.customer_ref || job.do_no ? 'ready' : 'partial',
                      summary: job.customer_ref || job.do_no || jobId,
                      confidence: job.customer_ref || job.do_no ? 0.92 : 0.7,
                    },
                    intelligence: {
                      label: 'Agent Intel',
                      status: hasError ? 'partial' : 'ready',
                      summary: hasError ? 'agent logged errors' : `${job.agent_log.length} agent steps completed`,
                      confidence: hasError ? 0.65 : 0.9,
                    },
                    harness: {
                      label: 'Execution',
                      status: job.status === 'completed' || job.status === 'delivered' ? 'ready' : 'waiting',
                      summary: job.status ?? 'unknown',
                      confidence: 0.85,
                    },
                  },
                };
                return <Gen5LayerStatusCard envelope={envelope} compact className="mb-4" />;
              })()}
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {job.agent_log.map((entry, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                          {entry.agent[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-primary">{entry.agent}</span>
                            <span className="text-[10px] text-muted-foreground">{entry.time}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{entry.msg}</p>
                        </div>
                      </div>
                    ))}
                    {timelineEvents.length > 0 ? (
                      <>
                        <Separator />
                        <div className="text-xs font-semibold">Job Events With GPS</div>
                        {timelineEvents.map((entry) => (
                          <div key={entry.id} className="rounded-lg border p-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="text-xs font-semibold">{entry.title}</div>
                              <div className="text-[10px] text-muted-foreground">{formatDateTime(entry.timestamp)}</div>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Trip {entry.tripType || '—'} · {entry.description || entry.eventType || '—'}
                            </div>
                            <div className="mt-1 text-xs font-mono">
                              GPS {formatCoords(entry.lat, entry.lng)}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : null}
                  </div>
                </CardContent>
                {job.agent_log.length > 0 && (
                  <div className="border-t px-4 py-2 flex justify-end">
                    <Link
                      to={`/brain/replay?runId=job:tms:${jobId}`}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      View in Replay Comparison →
                    </Link>
                  </div>
                )}
              </Card>
            </TabsContent>
            <TabsContent value="files" className="mt-4">
              <FileSharePanel entityType="job" entityId={jobId} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: Milestones */}
        <div className="space-y-3 lg:pt-11">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" /> Milestones
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1">
                {(timelineMilestones.length > 0 ? timelineMilestones : job.milestones).map((m, i) => (
                  <div key={('id' in m && m.id) ? m.id : i} className={`flex items-start gap-2.5 py-1.5 ${'done' in m && !m.done ? 'opacity-40' : ''}`}>
                    <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full flex items-center justify-center ${
                      ('done' in m ? m.done : true) ? 'bg-green-500' : 'bg-muted border-2 border-border'
                    }`}>
                      {('done' in m ? m.done : true) && <CheckCircle2 className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium">{'title' in m ? m.title : m.label}</div>
                      {(('done' in m ? m.done : true)) && (
                        <div className="text-[10px] text-muted-foreground space-y-0.5">
                          <div>
                            {'timestamp' in m ? formatDateTime(m.timestamp) : m.time} {('agent' in m && m.agent) ? `· ${m.agent}` : ''}
                          </div>
                          {'lat' in m && 'lng' in m && (m.lat != null && m.lng != null) ? (
                            <div className="font-mono">GPS {formatCoords(m.lat, m.lng)}</div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pickup & Delivery maps — inline below milestones */}
              <MilestoneMapPanel
                jobId={jobId}
                driverName={job.driver_name}
                driverPhone={job.driver_phone}
                vehicleNo={job.driver_plate}
                pickupLocation={job.port || job.terminal}
                deliveryLocation={job.delivery_address}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
